import { List } from 'immutable';
import Raven from 'raven-js';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Set as ImmutableSet } from 'immutable';
import { useQuery } from 'data-fetching-client';
import { pluck, partition, flatten } from 'underscore';
import MeetingsLink from 'MeetingsBase/models/MeetingsLink';
import UsageTracker from 'MeetingsAdmin/tracking/UsageTracker';
import linkTableQuery from '../../query/linkTableQuery';
import useQuickFetchQuery from '../../hooks/useQuickFetchQuery';
import useQueryParams from '../../hooks/useQueryParams';
// @ts-expect-error untyped import
import { getIsJitaUser } from '../../selectors/AccessSelector';
import { getUserId, getHasEditAllLinksScope
// @ts-expect-error untyped import
} from '../../selectors/AuthSelector';
import { ALL_MEETINGS_USERS } from '../../constants/enums';
import batchUsersQuery from '../../query/batchUsersQuery';
import linkStatsQuery from '../../query/linkStatsQuery';
import MeetingsLinksTableV2 from './MeetingsLinksTableV2';
import { useSchedulingPageSearch } from '../../hooks/useSchedulingPageSearch';
import { jsx as _jsx } from "react/jsx-runtime";
const FilterNamesForTracking = {
  OWNER: 'owner',
  TYPE: 'type',
  BUSINESS_UNIT: 'business unit'
};
const trackMeetingsFiltering = filterName => {
  UsageTracker.track('meetingsFiltering', {
    action: `filter ${filterName}`
  });
};
export const PAGINATION_COUNT = 10;
const useUserIdQueryParam = () => {
  const query = useQueryParams();
  const {
    userId
  } = query;
  return userId && typeof userId === 'string' ? parseInt(userId, 10) : undefined;
};
const useTransferOwnerQueryParam = () => {
  const query = useQueryParams();
  const {
    transferOwner
  } = query;
  return transferOwner && typeof transferOwner === 'string' ? parseInt(transferOwner, 10) : undefined;
};
function MeetingsLinkTableWrapper({
  handleRouteChange
}) {
  const canEditAllLinks = useSelector(getHasEditAllLinksScope);
  const isJitaUser = useSelector(getIsJitaUser);
  const userIdParam = useUserIdQueryParam();
  const transferOwnerParam = useTransferOwnerQueryParam();
  const initialUserId = userIdParam || (isJitaUser ? ALL_MEETINGS_USERS : null);
  const currentUserId = useSelector(getUserId);
  const [selectedUserId, setSelectedUserId] = useState(initialUserId);
  const [selectedMeetingType, setSelectedMeetingType] = useState(null);
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [offset, setOffset] = useState(0);
  const onFilterChange = useCallback(({
    userId,
    meetingType,
    businessUnitId,
    search
  }) => {
    if (search !== undefined) {
      setSearchInputValue(search);
    }
    if (userId !== undefined) {
      trackMeetingsFiltering(FilterNamesForTracking.OWNER);
      setSelectedUserId(userId);
    }
    if (meetingType !== undefined) {
      trackMeetingsFiltering(FilterNamesForTracking.TYPE);
      setSelectedMeetingType(meetingType);
    }
    if (businessUnitId !== undefined) {
      trackMeetingsFiltering(FilterNamesForTracking.BUSINESS_UNIT);
      setSelectedBusinessUnitId(businessUnitId);
    }
    setOffset(0);
  }, []);
  const clearFilters = useCallback(() => {
    setSelectedUserId(initialUserId);
    setSelectedMeetingType(null);
    setSelectedBusinessUnitId(null);
    setSearchInputValue('');
  }, [initialUserId]);
  useEffect(() => {
    if (selectedUserId === ALL_MEETINGS_USERS) {
      setOffset(0);
    }
  }, [selectedUserId]);
  const useHydratedLinksData = useMemo(() => selectedBusinessUnitId === null && selectedMeetingType === null && searchInputValue === '', [searchInputValue, selectedBusinessUnitId, selectedMeetingType]);
  const {
    schedulingPages: filteredSchedulingPages,
    loadingSchedulingPages: loadingFilteredSchedulingPages,
    total: totalFilteredSchedulingPages,
    schedulingPagesError: filteredSchedulingPagesError
  } = useSchedulingPageSearch({
    isUngated: true,
    meetingType: selectedMeetingType,
    searchQuery: searchInputValue,
    businessUnitId: selectedBusinessUnitId,
    ownerId: selectedUserId === ALL_MEETINGS_USERS ? null : selectedUserId || currentUserId,
    count: selectedUserId === ALL_MEETINGS_USERS && useHydratedLinksData ? PAGINATION_COUNT : null,
    offset: selectedUserId === ALL_MEETINGS_USERS ? offset : null
  });
  const filteredLinkIds = useMemo(() =>
  // When filtering for all users, we need to fetch non-paginated meeting links
  filteredSchedulingPages && selectedUserId === ALL_MEETINGS_USERS && !useHydratedLinksData ? pluck(filteredSchedulingPages, 'id') : [], [filteredSchedulingPages, selectedUserId, useHydratedLinksData]);
  const {
    data: hydratedSchedulingPages,
    previousData: previousHydratedSchedulingPages,
    refetch: refetchHydratedSchedulingPages,
    loading: loadingHydratedSchedulingPages
  } = useQuickFetchQuery(linkTableQuery, {
    fetchPolicy: 'cache-and-network',
    variables: {
      userId: selectedUserId,
      offset,
      count: PAGINATION_COUNT,
      search: '',
      linkIds: filteredLinkIds
    },
    quickFetchName: 'meetings-links',
    quickFetchTransform: links => ({
      results: links.map(link => MeetingsLink.fromMeetingsLink(link))
    })
  });
  const handleRefetch = useCallback(() => {
    refetchHydratedSchedulingPages().catch(error => Raven.captureException(error, {
      extra: {
        message: 'Error refetching scheduling pages'
      }
    }));
  }, [refetchHydratedSchedulingPages]);
  const meetingsLinksData = useMemo(() => {
    if (useHydratedLinksData) {
      if (hydratedSchedulingPages) {
        return flatten(partition(hydratedSchedulingPages.meetingsLinksTable.results, link => link.isDefaultLink));
      }
      if (previousHydratedSchedulingPages) {
        return flatten(partition(previousHydratedSchedulingPages.meetingsLinksTable.results, link => link.isDefaultLink));
      }
    }
    const hydratedData = hydratedSchedulingPages || previousHydratedSchedulingPages;
    if (filteredSchedulingPages && hydratedData) {
      return filteredSchedulingPages.map(schedulingPage => {
        const hydratedLink = hydratedData.meetingsLinksTable.results.find(hydrated => hydrated.id === schedulingPage.id);
        let link = new MeetingsLink({
          businessUnitId: schedulingPage.businessUnitId,
          id: schedulingPage.id,
          isDefaultLink: schedulingPage.isDefaultLink,
          name: schedulingPage.name,
          paymentLinkId: schedulingPage.paymentLinkId,
          slug: schedulingPage.slug,
          type: schedulingPage.type,
          userId: schedulingPage.userId
        });
        link = link.set('customParams', link.customParams.set('durations', List(schedulingPage.customParams.durations)));
        if (hydratedLink) {
          link = link.mergeDeep(hydratedLink.convertToRecordJSON());
          // businessUnitId and paymentLinkId coming from the backend for scheduling pages is unset
          link = link.set('businessUnitId', schedulingPage.businessUnitId);
          link = link.set('paymentLinkId', schedulingPage.paymentLinkId);
        }
        return link;
      });
    }
    return [];
  }, [filteredSchedulingPages, hydratedSchedulingPages, previousHydratedSchedulingPages, useHydratedLinksData]);
  const userIds = useMemo(() => meetingsLinksData && ImmutableSet(meetingsLinksData.map(link => link.get('userId'))).toArray(), [meetingsLinksData]);
  const {
    data: batchUsersData
  } = useQuery(batchUsersQuery, {
    skip: !meetingsLinksData,
    variables: {
      userIds
    }
  });
  const linkIds = useMemo(() => meetingsLinksData && ImmutableSet(meetingsLinksData.map(link => link.get('id'))).toArray(), [meetingsLinksData]);
  const {
    data: linkStatsData
  } = useQuery(linkStatsQuery, {
    skip: linkIds && linkIds.length === 0,
    variables: {
      linkIds
    }
  });
  const paginationTotal = useMemo(() => {
    if (selectedUserId === ALL_MEETINGS_USERS) {
      if (useHydratedLinksData && hydratedSchedulingPages) {
        return hydratedSchedulingPages.meetingsLinksTable.total;
      }
      return totalFilteredSchedulingPages;
    }
    return 0;
  }, [hydratedSchedulingPages, selectedUserId, totalFilteredSchedulingPages, useHydratedLinksData]);
  return /*#__PURE__*/_jsx(MeetingsLinksTableV2, {
    handleRouteChange: handleRouteChange,
    currentUserId: currentUserId,
    userIdParam: userIdParam,
    transferOwnerParam: transferOwnerParam,
    isJitaUser: isJitaUser,
    meetingsLinks: meetingsLinksData,
    refetchLinks: handleRefetch,
    linkStatsData: linkStatsData,
    batchUsersData: batchUsersData,
    filterValues: {
      selectedUserId,
      selectedMeetingType,
      selectedBusinessUnitId,
      searchInputValue
    },
    onFilterChange: onFilterChange,
    clearFilters: clearFilters,
    pagination: {
      total: paginationTotal,
      offset,
      setOffset
    },
    canEditAllLinks: canEditAllLinks,
    isLoading: loadingFilteredSchedulingPages || loadingHydratedSchedulingPages || !meetingsLinksData,
    hasError: !!filteredSchedulingPagesError
  });
}
export default MeetingsLinkTableWrapper;